import { Component, Input, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ISave } from '@app-settings/shared/models/i-save.model';
import { SettingsService } from '@app-settings/shared/settings.service';
import { BaseComponent } from '@core/components/base-component';
import { License } from '@interfaces/LicenseController';
import { TranslateService } from '@ngx-translate/core';
import { LicenseService } from '@shared/services/license.service';
import { DateTime } from 'luxon';
import { NGXLogger } from 'ngx-logger';
import { Message } from 'primeng/api';
import { firstValueFrom, forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  selector: 'pop-services-licenses-settings',
  templateUrl: './services-licenses-settings.component.html',
  styleUrls: ['../settings.component.less', './services-licenses-settings.component.less']
})
export class ServicesLicensesSettingsComponent extends BaseComponent implements OnInit, ISave {
  public license: File;
  public messages: Message[] = [];

  private _verifiedLicense: License;
  @Input()
  public set verifiedLicense(value) {
    this._verifiedLicense = value;
    this.checkLicenseLimits();
    this.checkModuleExpiration();
    this.messages = [];
    this.cancelLicense();
  }

  public get verifiedLicense() {
    return this._verifiedLicense;
  }

  public constructor(
    private licenseService: LicenseService,
    private snackBar: MatSnackBar,
    private logger: NGXLogger,
    private translateService: TranslateService,
    private popSettingsService: SettingsService
  ) {
    super();
  }

  public ngOnInit() {
    //get settings modules and set default view to open based on the license modules
    this.subscribe(
      this.licenseService.getLicense().subscribe(license => {
        if (!license) {
          return;
        }
        this.verifiedLicense = license;
      })
    );
  }

  public uploadLicense(event: Event) {
    const input = event.target as HTMLInputElement;
    this.license = input.files[0];
    this.onChange();
  }

  public cancelLicense() {
    this.license = null;
  }

  public showError(error: string): void {
    this.translateService.get([error, 'BUTTON.CLOSE']).subscribe(([errorMsg, closeButton]) =>
      this.snackBar.open(errorMsg, closeButton, {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'top'
      })
    );
  }

  private checkLicenseLimits(): void {
    forkJoin(
      this.verifiedLicense.modules.map(module => this.licenseService.isTooManyUsersForModule(module.moduleId, false))
    ).subscribe((res: boolean[]) => {
      if (res.includes(true)) {
        this.showBanner('error', 'ERROR.LICENSE_TOO_MANY_USERS');
      }
    });
  }

  private checkModuleExpiration(): void {
    const moduleExpired = this.verifiedLicense.modules
      .map(module => DateTime.now() > DateTime.fromISO(module.expiration))
      .includes(true);

    if (moduleExpired) {
      this.showBanner('error', 'ERROR.LICENSE_EXPIRED');
      return;
    }

    const moduleExpiresSoon = this.verifiedLicense.modules
      .map(module => module.expiration && DateTime.now().plus({ months: 1 }) > DateTime.fromISO(module.expiration))
      .includes(true);

    if (moduleExpiresSoon) {
      this.showBanner('warn', 'ERROR.LICENSE_EXPIRING');
    }
  }

  private showBanner(severity: string, detail: string): void {
    this.translateService.get(detail).subscribe(d => {
      const messageObj: Message = { severity: severity, detail: d };
      if (this.messages.findIndex(m => m.detail === messageObj.detail) === -1) {
        this.messages.push(messageObj);
      }
    });
  }

  public onChange() {
    this.popSettingsService.needsSave();
    this.popSettingsService.onSave(this);
  }

  public async onSave() {
    if (this.license) {
      try {
        this.verifiedLicense = await firstValueFrom(this.licenseService.uploadLicense(this.license).pipe(take(1)));
        location.reload(); // refresh page (for now)
      } catch (error) {
        this.logger.error("Can't upload license", error);
        this.showError('ERROR.LICENSE_INVALID');
        this.logger.error(JSON.stringify(error));
      }
    }
  }
}
